import { Paper, Table, TableBody, TableContainer, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import BidDetailActivationGroupCell from '@/features/bidding/components/bidDetail/table/BidDetailActivationGroupCell'
import BidDetailPtuCell from '@/features/bidding/components/bidDetail/table/BidDetailPtuCell'
import BidDetailStepRow from '@/features/bidding/components/bidDetail/table/BidDetailStepRow'
import BidDetailTableHeadRow from '@/features/bidding/components/bidDetail/table/BidDetailTableHeadRow'
import type { BidDetailColumnGroup, BidDetailDataColumn } from '@/features/bidding/components/bidDetail/table/types'
import type { BidEnergyPtu } from '@/features/bidding/types/bidDetail/BidEnergyPtu'

import { renderActivationGroupMeta, renderPtuMeta } from './BidEnergyMeta'
import BidEnergyStepCells from './BidEnergyStepCells'

export type BidEnergyStepsTableProps = Readonly<{
  ptus: ReadonlyArray<BidEnergyPtu>
  columnGroups: ReadonlyArray<BidDetailColumnGroup>
  dataColumns: ReadonlyArray<BidDetailDataColumn>
  ptuLengthInMinutes: number
  /** `false` when `/capacity-obligations` errored — OBL renders "—" instead of a number. */
  obligationsAvailable: boolean
  /** Optional max-height for the sticky-header scroll container. */
  maxHeight?: number | string
  /** Override the empty-state copy (defaults to the generic "no bids" message). */
  emptyMessage?: string
}>

/**
 * Energy steps × PTUs table. Walks the PTU → activation group → step tree and
 * lays it out with MUI `<Table>` primitives (not `CustomDataGrid`, which can't
 * rowspan). All view concerns live here: the rowspan'd PTU and activation-group
 * cells are emitted once per group, their rowspans are a plain count of the
 * (already-filtered) tree, and each row's key is composed from the node
 * identities.
 */
export default function BidEnergyStepsTable({
  ptus,
  columnGroups,
  dataColumns,
  ptuLengthInMinutes,
  obligationsAvailable,
  maxHeight,
  emptyMessage,
}: BidEnergyStepsTableProps) {
  const { t } = useTranslation()

  if (ptus.length === 0) {
    return (
      <Paper sx={{ padding: 4, textAlign: 'center' }} variant="outlined">
        <Typography color="text.secondary">{emptyMessage ?? t('bidding.results.no_bids')}</Typography>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight, overflow: 'auto' }} variant="outlined">
      <Table stickyHeader size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <BidDetailTableHeadRow columnGroups={columnGroups} dataColumns={dataColumns} />
        <TableBody>
          {ptus.flatMap((ptu) => {
            const ptuRowCount = ptu.activationGroups.reduce((sum, group) => sum + group.steps.length, 0)

            return ptu.activationGroups.flatMap((group, groupIndex) =>
              group.steps.map((ladderStep, stepIndex) => {
                const identityCells = (
                  <>
                    {groupIndex === 0 && stepIndex === 0 && (
                      <BidDetailPtuCell
                        dimmed={ptu.dimmed}
                        isNow={ptu.state.now}
                        label={ptu.start.toFormat('HH:mm')}
                        meta={renderPtuMeta(ptu.offeredWatts)}
                        rowSpan={ptuRowCount}
                        source={ptu.source}
                        subLabel={`${ptuLengthInMinutes} min`}
                      />
                    )}
                    {stepIndex === 0 && (
                      <BidDetailActivationGroupCell
                        code={group.code}
                        dimmed={group.dimmed}
                        meta={renderActivationGroupMeta(
                          group.obligationWatts,
                          group.offeredWatts,
                          obligationsAvailable,
                        )}
                        rowSpan={group.steps.length}
                        subLabel={`${group.steps.length} steps`}
                      />
                    )}
                  </>
                )

                return (
                  <BidDetailStepRow
                    key={`${ptu.start.toMillis()}-${group.uuid}-${ladderStep.index}`}
                    dataCells={<BidEnergyStepCells dimmed={ladderStep.isZero} step={ladderStep.step} />}
                    dimmed={ladderStep.isZero}
                    identityCells={identityCells}
                    state={ptu.state}
                    stepIndex={ladderStep.index + 1}
                  />
                )
              }),
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
